import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

/**
 * 🔍 Logging Service
 *
 * Serviço centralizado para gestão de logs condicionais.
 * Permite ativar/desativar logs dinamicamente em produção
 * baseado em permissões de admin.
 */
@Injectable({
  providedIn: 'root',
})
export class LoggingService {
  private originalConsole: {
    log: any;
    debug: any;
    info: any;
    warn: any;
    error: any;
  };

  private logsEnabled = false;

  constructor() {
    // Preservar métodos originais do console
    // Em produção, usar os que foram preservados no main.ts
    const savedConsole = (window as any)['__originalConsole'];

    if (savedConsole) {
      // Usar console preservado pelo main.ts (produção)
      this.originalConsole = savedConsole;
    } else {
      // Desenvolvimento - preservar diretamente
      this.originalConsole = {
        log: console.log.bind(console),
        debug: console.debug.bind(console),
        info: console.info.bind(console),
        warn: console.warn.bind(console),
        error: console.error.bind(console),
      };
    }

    // Em desenvolvimento, logs sempre ativos
    if (!environment.production) {
      this.logsEnabled = true;
    }
  }

  /**
   * Ativa os logs (restaura métodos originais)
   */
  enableLogs(): void {
    this.logsEnabled = true;
    console.log = this.originalConsole.log;
    console.debug = this.originalConsole.debug;
    console.info = this.originalConsole.info;
    console.warn = this.originalConsole.warn;
    console.error = this.originalConsole.error;

    console.info('🔍 Logs de debug ativados (Admin Mode)');
  }

  /**
   * Desativa os logs (substitui por noop)
   */
  disableLogs(): void {
    this.logsEnabled = false;
    const noop = () => {};
    console.log = noop;
    console.debug = noop;
    console.info = noop;
    console.warn = noop;
    // Manter console.error sempre ativo para erros críticos do sistema
    // console.error = noop;

    this.originalConsole.info('🔇 Logs de debug desativados');
  }

  /**
   * Verifica se os logs estão ativos
   */
  isEnabled(): boolean {
    return this.logsEnabled;
  }

  /**
   * Log forçado (sempre aparece, mesmo com logs desativados)
   * Útil para mensagens críticas
   */
  forceLog(message: string, ...args: any[]): void {
    this.originalConsole.log(message, ...args);
  }

  /**
   * Aviso forçado (sempre aparece)
   */
  forceWarn(message: string, ...args: any[]): void {
    this.originalConsole.warn(message, ...args);
  }

  /**
   * Erro forçado (sempre aparece)
   */
  forceError(message: string, ...args: any[]): void {
    this.originalConsole.error(message, ...args);
  }

  /**
   * Obtém referência aos métodos originais
   */
  getOriginalConsole() {
    return this.originalConsole;
  }
}
